/**
 * Форматы данных — раздел «Форматы данных» кита.
 *
 * Единственное место, где числа и даты превращаются в текст.
 * API отдаёт ISO-даты и обычные числа; всё русское — здесь,
 * потому что это правила интерфейса, а не свойства данных.
 */

const NBSP = ' ';
const EMDASH = '—';

/**
 * Деньги — просто число, неразрывный пробел разрядами, знака валюты нет.
 * Копейки показываются, только если есть: «3 500», а не «3 500,00».
 */
export function money(value: number | null | undefined): string {
  if (value === null || value === undefined) return EMDASH;
  const hasKopecks = Math.round(value * 100) % 100 !== 0;
  return value
    .toLocaleString('ru-RU', {
      minimumFractionDigits: hasKopecks ? 2 : 0,
      maximumFractionDigits: 2,
    })
    .replace(/\s/g, NBSP);
}

/** Обычное число с разрядами. */
export function num(value: number | null | undefined): string {
  if (value === null || value === undefined) return EMDASH;
  return value.toLocaleString('ru-RU').replace(/\s/g, NBSP);
}

/** Дата в таблице — цифрами: 23.07.2026. Вход — ISO ГГГГ-ММ-ДД. */
export function date(iso: string | null | undefined): string {
  if (!iso) return EMDASH;
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

const MONTHS_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

/** Дата в тексте — словом: 23 июля 2026. */
export function dateLong(iso: string | null | undefined): string {
  if (!iso) return EMDASH;
  const [y, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS_GENITIVE[m - 1]} ${y}`;
}

/** Диапазон — типографское тире с пробелами. */
export function dateRange(from: string | null, to: string | null): string {
  if (!from && !to) return EMDASH;
  if (!to || from === to) return date(from);
  return `${date(from)} ${EMDASH} ${date(to)}`;
}

/** Процент без знака. */
export function percent(value: number | null | undefined): string {
  return value === null || value === undefined ? EMDASH : `${value}%`;
}

/** Дробь с пробелами: 10 / 25. */
export function fraction(a: number, b: number): string {
  return `${num(a)} / ${num(b)}`;
}

/** Нет данных — тире. */
export const dash = EMDASH;

/**
 * Склонения. Русский требует трёх форм: «5 ученик» и «1 учеников» —
 * брак, который заметит любой пользователь, а в счётчиках это
 * на каждом экране.
 */
export function plural(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return many;
  if (last > 1 && last < 5) return few;
  if (last === 1) return one;
  return many;
}

export const withPlural = (n: number, one: string, few: string, many: string): string =>
  `${num(n)}${NBSP}${plural(n, one, few, many)}`;

// Готовые формы модуля — чтобы склонения не переписывались на каждом экране
export const teams = (n: number) => withPlural(n, 'команда', 'команды', 'команд');
export const nominations = (n: number) => withPlural(n, 'номинация', 'номинации', 'номинаций');
export const services = (n: number) => withPlural(n, 'услуга', 'услуги', 'услуг');
export const classes = (n: number) => withPlural(n, 'класс', 'класса', 'классов');
export const sales = (n: number) => withPlural(n, 'продажа', 'продажи', 'продаж');
export const positions = (n: number) => withPlural(n, 'позиция', 'позиции', 'позиций');
export const packages = (n: number) => withPlural(n, 'пакет', 'пакета', 'пакетов');
export const people = (n: number) => withPlural(n, 'участник', 'участника', 'участников');
export const checks = (n: number) => withPlural(n, 'проверка', 'проверки', 'проверок');

/** Хронометраж номера: 2:30. */
export function mmss(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return EMDASH;
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * Тайминг-план блока: 7 ч 04 мин. Часы появляются только когда есть,
 * иначе «0 ч 45 мин» читается как ошибка.
 */
export function duration(seconds: number | null | undefined): string {
  if (!seconds) return EMDASH;
  const minutes = Math.round(seconds / 60);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return `${m}${NBSP}мин`;
  return `${h}${NBSP}ч ${String(m).padStart(2, '0')}${NBSP}мин`;
}

/** Возрастные границы номинации: «12–15», «16+», «до 15». */
export function ageRange(from: number | null, to: number | null): string {
  if (from !== null && to !== null) return `${from}–${to}`;
  if (from !== null) return `${from}+`;
  if (to !== null) return `до ${to}`;
  return EMDASH;
}

/** Время блока или класса: «09:00 — 10:20», «время не задано». */
export function timeRange(from: string | null, to: string | null): string | null {
  if (from && to) return `${from} ${EMDASH} ${to}`;
  if (from) return from;
  return null;
}

/**
 * Разбор даты, набранной руками: `27.02.2027` → `2027-02-27`.
 *
 * Нужен только в модальных окнах: там календарь не ставится — он
 * поповер, а поповер внутри окна обрезается его `overflow:hidden`
 * (ТЗ §7, «конфликт слоёв»). Везде, где окна нет, дата выбирается
 * календарём и руками не набирается.
 */
export function parseRuDate(value: string): string | null {
  const m = value.trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!m) return null;

  const [, dd, mm, yyyy] = m;
  const iso = `${yyyy}-${mm}-${dd}`;

  // 31.02 разбирается, но такой даты не существует — проверяем обратным ходом
  const probe = new Date(`${iso}T00:00:00.000Z`);
  if (Number.isNaN(probe.getTime()) || probe.toISOString().slice(0, 10) !== iso) return null;

  return iso;
}

/** Все дни мероприятия: от даты начала до даты окончания включительно. */
export function daysOfEvent(dateFrom: string, dateTo: string): string[] {
  const days: string[] = [];
  const end = new Date(`${dateTo}T00:00:00.000Z`).getTime();

  for (let d = new Date(`${dateFrom}T00:00:00.000Z`); d.getTime() <= end; ) {
    days.push(d.toISOString().slice(0, 10));
    d = new Date(d.getTime() + 86_400_000);
  }
  return days;
}
